// Auth client state — the session tokens and derived auth flag. The tokens are
// the source of truth for "logged in" across the whole app: the router guard
// and the axios interceptors both read the same localStorage keys, so this
// store writes through to them (never a parallel copy). Refs mirror the keys so
// components can react to login / logout without polling storage.
//
// Server state (the UAE Pass flow's requests) lives in TanStack Query — this
// store only holds the resulting session.
import { computed, ref } from 'vue'
import { defineStore } from 'pinia'
import localStorageService from '@core/persistence/localStorage/localStorageService'
import { AUTH_STORAGE_KEYS } from '@core/persistence/storageKeys'

export const useAuthStore = defineStore('auth', () => {
  const token = ref<string | null>(localStorageService.getItem(AUTH_STORAGE_KEYS.TOKEN))
  const refreshToken = ref<string | null>(
    localStorageService.getItem(AUTH_STORAGE_KEYS.REFRESH_TOKEN),
  )

  const isAuthenticated = computed(() => Boolean(token.value))

  // Persist a freshly issued session (UAE Pass login or sign-up). Writes the two
  // keys the interceptors + guard read, so the rest of the app is logged in.
  function setSession(accessToken: string, newRefreshToken: string): void {
    token.value = accessToken
    refreshToken.value = newRefreshToken
    localStorageService.setItem(AUTH_STORAGE_KEYS.TOKEN, accessToken)
    localStorageService.setItem(AUTH_STORAGE_KEYS.REFRESH_TOKEN, newRefreshToken)
  }

  function clearSession(): void {
    token.value = null
    refreshToken.value = null
    localStorageService.removeItem(AUTH_STORAGE_KEYS.TOKEN)
    localStorageService.removeItem(AUTH_STORAGE_KEYS.REFRESH_TOKEN)
  }

  return { token, refreshToken, isAuthenticated, setSession, clearSession }
})
